import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'

export const useUsers = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const useUsersQuery = (filter) =>
    useQuery({
      queryKey: ['getUsersQuery', filter],
      queryFn: ({ queryKey }) => api.post('user/overview', { filter: queryKey[1] }),
    })

  const useRolesMetaQuery = () =>
    useQuery({ queryKey: ['getRolesMetaQuery'], queryFn: () => api.post('role/metadata') })

  const saveUserRolesMutation = useMutation({
    mutationKey: ['saveUserRolesMutation'],
    mutationFn: (roles) => api.post('user/role/set', roles),
  })

  const createUserMutation = useMutation({
    mutationKey: ['createUserMutation'],
    mutationFn: (user) => api.post('user/create', { ...user, password: '', key: undefined }),
    onSuccess: async (response, request) => {
      const newUser = { ...response, ...request }
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ['getUsersQuery'],
      })

      // Optimistically update to the new value
      queryClient.setQueriesData({ queryKey: ['getUsersQuery'] }, (oldData) => {
        return {
          ...oldData,
          rows: [...oldData.rows, newUser],
        }
      })

      // Return a context with the previous and new todo
      return { newUser }
    },
    onError: (err, newSite, context) => {
      queryClient.setQueriesData({ queryKey: ['getUsersQuery'] }, context.previousQueries)
    },
  })

  const updateUserMutation = useMutation({
    mutationKey: ['updateUserMutation'],
    mutationFn: (user) => api.post('user/update', user),
    onError: (err, newUser, context) => {
      queryClient.invalidateQueries({
        queryKey: ['getUsersQuery'],
      })
    },
  })

  const deleteUserMutation = useMutation({
    mutationKey: ['deleteUserMutation'],
    mutationFn: (user_id) => api.post('user/delete', { user_id }),
  })

  const sendResetEmailRequest = useMutation({
    mutationKey: ['sendResetEmailQuery'],
    mutationFn: (email_or_username) =>
      api.post(
        `otp/send_reset_email?email_or_username=${email_or_username}`,
        { email_or_username },
        { ignoreDefaults: true }
      ),
  })

  const validateOTPRequest = useMutation({
    mutationKey: ['validateOTPRequest'],
    mutationFn: (one_time_password) =>
      api.post(
        `otp/validate_reset_token?one_time_password=${one_time_password}`,
        { one_time_password },
        { ignoreDefaults: true }
      ),
  })

  const validatePasswordRequest = useMutation({
    mutationKey: ['validatePasswordRequest'],
    mutationFn: ({ username, password }) => {
      if (!password) password = '%20'
      return api.post(`validate_password?password=${password}&username=${username}`, undefined, {
        ignoreDefaults: true,
      })
    },
  })

  const resetPasswordMutation = useMutation({
    mutationKey: ['resetPasswordMutation'],
    mutationFn: ({ one_time_password, new_password }) =>
      api.post(
        `otp/reset_password?one_time_password=${one_time_password}&new_password=${new_password}`,
        {
          one_time_password,
          new_password,
        },
        { ignoreDefaults: true }
      ),
  })

  const updateDriverPWMutation = useMutation({
    mutationKey: ['updateDriverPWMutation'],
    mutationFn: ({ username, new_password }) => api.post('user/update_driver_password', { username, new_password }),
  })

  return {
    useUsersQuery,
    useRolesMetaQuery,
    saveUserRolesMutation,
    createUserMutation,
    updateUserMutation,
    deleteUserMutation,
    sendResetEmailRequest,
    validateOTPRequest,
    validatePasswordRequest,
    resetPasswordMutation,
    updateDriverPWMutation,
  }
}
